import { useContext, useEffect, useState } from 'react';
import { cdcSite } from '../store/StaticVariables';
import { useAfterInit } from '../hooks/useAfterInit';
import { InterfaceContext } from '../store/InterfaceProvider';
import { GetAllNationalData, getRecent12Months } from '../requests/NationalFeatureRequest';
import { ReloadSelectedFeatureRequest } from '../requests/ReloadSelectedFeatureRequest';
import defaultFeature from '../store/DefaultFeature.json';
import SnapShotText from './SnapshotText';
import { generateDisplayAttribute, geometryScaleFromGeoid } from '../utils/DataTransformation';

const Snapshot = () => {
    const { activeTimePeriod, selectedFeature, setSelectedFeature, setSearchResult, setAppError, activeViewType, activeDeathType, setPast12Months, nationalData, token } = useContext(InterfaceContext);
    const [snapshotFeature, setSnapshotFeature] = useState(defaultFeature);
    const [nationalFeature, setNationalFeature] = useState(defaultFeature);
    // const [nationalData, setNationalData] = useState({});

    const usExtent = {
        xmin: -14150703.25072198,
        ymin: 1724460.655959669,
        xmax: -7075872.59319234,
        ymax: 6815350.164313462
    };
    const usExtentWkid = 3857;

    const getActiveNationalFeature = () => {
        return nationalData[activeTimePeriod];
    };
    const clearFeature = () => {
        setSnapshotFeature(nationalFeature);
        setSelectedFeature({});
        setSearchResult({ address: '*', extent: usExtent, wkid: usExtentWkid });
    };
    const getActiveRateModeledAttribute = () => {
        return generateDisplayAttribute({ type: activeViewType, deathType: activeDeathType, measurement: 'rate', modeled: true });
    };
    const getFeatureModeledValue = (feature) => {
        const attr = getActiveRateModeledAttribute();
        return feature[attr];
    };
    const getIsProvisionalData = () => {
        if (activeViewType !== 'deaths') return false;
        return activeTimePeriod === 'ttm' || activeTimePeriod === '2023';
    };

    const showModelRatesText = () => {
        return `Rates are modeled to protect privacy and improve stability, such as when there are only a few ${activeViewType !== 'ed_visits' ? activeViewType : 'emergency department visits'} (1-9) in an area.`;
    };

    // useEffect(() => {
    //     if (!token) return;
    //     getRecent12Months({ token })
    //         .then(setPast12Months)
    //         .catch((error) => {
    //             setAppError(true, 'Recent 12 Months', error);
    //         });
    //     GetAllNationalData({ token })
    //         .then(setNationalData)
    //         .catch((error) => {
    //             setAppError(true, 'Snapshot', error);
    //             setNationalData([]);
    //         });
    // }, [token]);

    //when time period changes update selected feature
    useAfterInit(() => {
        if (selectedFeature.GEOID + '' !== '1' && selectedFeature.GEOID + '' !== '0') {
            ReloadSelectedFeatureRequest({ feature: selectedFeature, time: activeTimePeriod, token })
                .then((newFeature) => {
                    setSelectedFeature(newFeature || {});
                })
                .catch((error) => {
                    setSelectedFeature({});
                    setAppError(true, 'Reload Snapshot Error', error);
                });
        }
    }, [activeTimePeriod]);

    useAfterInit(() => {
        if (selectedFeature.GEOID < 2) {
            setSelectedFeature(nationalFeature);
        }
    }, [nationalFeature]);

    //when time period changed and once national features are loaded, set the default national feature
    useAfterInit(() => {
        if (!Object.keys(nationalData).length) return;
        setNationalFeature(getActiveNationalFeature());
    }, [nationalData, activeTimePeriod]);

    //when selected Feature or national feature changes update the snapshotFeature
    useAfterInit(() => {
        if (selectedFeature.GEOID + '' === '0' && nationalFeature.GEOID + '' !== '0') {
            setSelectedFeature(nationalFeature);
        } else {
            setSnapshotFeature(selectedFeature);
        }
    }, [selectedFeature, nationalFeature]);

    const featureScale = geometryScaleFromGeoid(snapshotFeature.GEOID);
    const rateIsModeled = getFeatureModeledValue(snapshotFeature) === 1; //if rate_modeled
    const grouped = (snapshotFeature.agg_count && snapshotFeature.agg_count > 1) || false;
    const isProvisionalData = getIsProvisionalData();
    const showCoverageDisclosure = (date) => {
        let dynamicStatement = `In ${activeTimePeriod !== 'ttm' ? activeTimePeriod : 'recent 12 months'}, less than 50% of ${snapshotFeature.st_name ?? snapshotFeature.name} emergency departments reported data.`;
        let datesObj = {
            ttm: () => {
                if (['California', 'Oklahoma'].includes(snapshotFeature.st_name ?? snapshotFeature.name)) {
                    return dynamicStatement;
                }
            },
            2021: () => {
                if (['California', 'Hawaii', 'Oklahoma', 'Minnesota', 'Iowa'].includes(snapshotFeature.st_name ?? snapshotFeature.name)) {
                    return dynamicStatement;
                }
            },
            2022: () => {
                if (['California', 'Hawaii', 'Oklahoma', 'Minnesota', 'Iowa'].includes(snapshotFeature.st_name ?? snapshotFeature.name)) {
                    return dynamicStatement;
                }
            },
            2023: () => {
                if (['California', 'Hawaii', 'Oklahoma', 'Minnesota'].includes(snapshotFeature.st_name ?? snapshotFeature.name)) {
                    return dynamicStatement;
                }
            },
            default: () => {
                return ``;
            }
        };
        return (datesObj[date] || datesObj['default'])();
    };
    const aboutLink = (hash) => (
        <>
            <a href={cdcSite + '/injury-violence-data/about/index.html' + hash} target="_blank">
                Read More &raquo;
            </a>
        </>
    );

    return (
        <div id="snapshot">
            {snapshotFeature.GEOID + '' !== '0' && (
                <>
                    <div id="feature-info">
                        {snapshotFeature.GEOID + '' !== '1' && (
                            <div id="clear-feature" onClick={clearFeature}>
                                <span className="atag">Clear selection and reset to U.S. view</span>
                            </div>
                        )}
                        <SnapShotText feature={snapshotFeature} showDetails={true} />
                    </div>
                    <div id="snapshot-sidebar">
                        {activeViewType === 'ed_visits' && (
                            <div className="snapshot-side-panel">
                                <div className="agg-inner">
                                    <p>
                                        <strong>
                                            <span className="icon warn"></span>Emergency Department Data Note
                                        </strong>
                                    </p>
                                    <p>Emergency department visit data is provided by hospitals. Rates should be considered an estimate because there are differences in hospital participation and coding practices.</p>
                                    <p>{showCoverageDisclosure(activeTimePeriod)}</p>
                                    <p>{aboutLink('#')}</p>
                                </div>
                            </div>
                        )}
                        {grouped && featureScale === 'census' && (
                            <div className="snapshot-side-panel">
                                <div className="agg-inner">
                                    <p>
                                        <strong>
                                            <span className="icon info"></span>Grouped Census Tracts
                                        </strong>
                                    </p>
                                    <p>The census tracts for the selected area are combined to protect privacy and improve stability of rates. </p>
                                    <p>{aboutLink('#')}</p>
                                </div>
                            </div>
                        )}
                        {grouped && featureScale === 'zcta' && (
                            <div className="snapshot-side-panel">
                                <div className="agg-inner">
                                    <p>
                                        <strong>
                                            <span className="icon info"></span>Grouped ZIP Codes
                                        </strong>
                                    </p>
                                    <p>The ZIP codes for the selected area are combined to protect privacy and improve stability of rates.</p>
                                    <p>{aboutLink('#')}</p>
                                </div>
                            </div>
                        )}
                        {rateIsModeled && (
                            <div className="snapshot-side-panel">
                                <div className="agg-inner">
                                    <p>
                                        <strong>
                                            <span className="icon info"></span>Modeled Rates
                                        </strong>
                                    </p>
                                    <p>{showModelRatesText()}</p>
                                    <p>{aboutLink('#')}</p>
                                </div>
                            </div>
                        )}
                        {isProvisionalData && activeTimePeriod !== "2023" &&(
                            <div className="snapshot-side-panel">
                                <div className="agg-inner">
                                    <p>
                                        <strong>
                                            <span className="icon info"></span>Provisional Data
                                        </strong>
                                    </p>
                                    <p>This is provisional data. Provisional data is not final and may not yet be complete.</p>
                                    <p>{aboutLink('#')}</p>
                                </div>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};
export default Snapshot;
